import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { Subject, debounceTime } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { VendorService } from '../vendors/vendor.service';
import { PermissionService } from '../../core/services/permission.service';
import { ManufacturerService } from '../manufacturers/manufacturers.service';
import {
  BulkVendorAssignRequest,
  BulkVendorFilter,
  BulkVendorPreview,
  BulkVendorRow,
  ProductsService,
} from '../products/products.service';

type VendorState = 'ALL' | 'NONE' | 'HAS';
type Mode = 'PRIMARY' | 'ADDITIONAL';

/**
 * Products > Vendor Mapping: filter the catalogue (mainly by brand), select
 * products, and set their vendor and/or vendor margin in one go. Built for a
 * catalogue loaded with brands but no vendors; "Current vendor" + replace also
 * covers moving a vendor's products to another vendor.
 */
@Component({
  selector: 'app-product-vendor-mapping',
  imports: [CommonModule, FormsModule, NgSelectModule],
  templateUrl: './product-vendor-mapping.component.html',
  styleUrl: './product-vendor-mapping.component.scss',
})
export class ProductVendorMappingComponent {
  private vendorService = inject(VendorService);
  private manufacturerService = inject(ManufacturerService);
  private productService = inject(ProductsService);
  private toastr = inject(ToastrService);
  private permissions = inject(PermissionService);

  /** VIEW access shows the list only. */
  get canEdit(): boolean {
    return this.permissions.canEdit('products.vendor-mapping');
  }

  // ── Masters ──
  vendors = signal<any[]>([]);
  brands = signal<any[]>([]);
  manufacturers = signal<any[]>([]);
  departments = signal<any[]>([]);

  // ── Filter ──
  brandIds: string[] = [];
  manufacturerIds: string[] = [];
  departmentIds: string[] = [];
  currentVendorId: string | null = null;
  vendorState = signal<VendorState>('ALL');
  search = '';
  private search$ = new Subject<void>();

  // ── Results ──
  rows = signal<BulkVendorRow[]>([]);
  total = signal(0);
  withoutVendor = signal(0);
  page = signal(1);
  pageCount = signal(0);
  readonly limit = 50;
  loading = signal(false);
  /** Filter the shown rows / "all matching" selection belong to. */
  private appliedFilter: BulkVendorFilter = {};

  // ── Selection: explicit ids, or every matching product minus exclusions ──
  selected = signal<Set<string>>(new Set());
  allMatching = signal(false);
  excluded = signal<Set<string>>(new Set());

  selectedCount = computed(() =>
    this.allMatching()
      ? Math.max(this.total() - this.excluded().size, 0)
      : this.selected().size,
  );
  pageAllChecked = computed(
    () => this.rows().length > 0 && this.rows().every((r) => this.isChecked(r)),
  );

  // ── Action ──
  vendorId: string | null = null;
  mode = signal<Mode>('PRIMARY');
  margin: number | null = null;
  onlyWithoutVendor = false;
  dropPreviousVendor = true;

  // ── Confirm dialog ──
  confirmOpen = signal(false);
  preview = signal<BulkVendorPreview | null>(null);
  previewLoading = signal(false);
  applying = signal(false);

  constructor() {
    this.vendorService.getAllVendorsList().subscribe({
      next: (v) =>
        this.vendors.set(
          [...(v ?? [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
        ),
    });
    this.manufacturerService.getAllManufacturersBrands().subscribe({
      next: (res: any) =>
        this.brands.set(Array.isArray(res) ? res : res?.data ?? []),
    });
    this.manufacturerService
      .getManufacturers({ page: 1, limit: 10000 })
      .subscribe({
        next: (res: any) =>
          this.manufacturers.set(
            [...(res?.data ?? [])].sort((a: any, b: any) =>
              (a.name || '').localeCompare(b.name || ''),
            ),
          ),
      });
    this.productService.getAllDepartments().subscribe({
      next: (res: any) =>
        this.departments.set(Array.isArray(res) ? res : res?.data ?? []),
    });

    this.search$
      .pipe(debounceTime(350), takeUntilDestroyed())
      .subscribe(() => this.runSearch());
    this.runSearch();
  }

  // ── Filtering / paging ──

  private buildFilter(): BulkVendorFilter {
    const f: BulkVendorFilter = { vendorState: this.vendorState() };
    if (this.brandIds.length) f.brandIds = [...this.brandIds];
    if (this.manufacturerIds.length) f.manufacturerIds = [...this.manufacturerIds];
    if (this.departmentIds.length) f.departmentIds = [...this.departmentIds];
    if (this.currentVendorId) f.currentVendorId = this.currentVendorId;
    if (this.search.trim()) f.search = this.search.trim();
    return f;
  }

  get hasFilter(): boolean {
    return !!(
      this.brandIds.length ||
      this.manufacturerIds.length ||
      this.departmentIds.length ||
      this.currentVendorId ||
      this.search.trim() ||
      this.vendorState() !== 'ALL'
    );
  }

  onFilterChange() {
    this.runSearch();
  }

  onSearchInput() {
    this.search$.next();
  }

  setVendorState(s: VendorState) {
    this.vendorState.set(s);
    this.runSearch();
  }

  clearFilters() {
    this.brandIds = [];
    this.manufacturerIds = [];
    this.departmentIds = [];
    this.currentVendorId = null;
    this.search = '';
    this.vendorState.set('ALL');
    this.runSearch();
  }

  /** New filter: back to page 1 and drop the selection (it described the old list). */
  private runSearch() {
    this.appliedFilter = this.buildFilter();
    this.clearSelection();
    this.load(1);
  }

  load(page: number) {
    this.loading.set(true);
    this.productService
      .bulkVendorList({ ...this.appliedFilter, page, limit: this.limit })
      .subscribe({
        next: (res) => {
          this.rows.set(res.data ?? []);
          this.total.set(res.total ?? 0);
          this.withoutVendor.set(res.withoutVendor ?? 0);
          this.page.set(res.page ?? page);
          this.pageCount.set(res.pageCount ?? 0);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.toastr.error('Could not load products');
        },
      });
  }

  // ── Selection ──

  isChecked(r: BulkVendorRow): boolean {
    return this.allMatching()
      ? !this.excluded().has(r.id)
      : this.selected().has(r.id);
  }

  toggleRow(r: BulkVendorRow) {
    const key = this.allMatching() ? this.excluded : this.selected;
    const next = new Set(key());
    next.has(r.id) ? next.delete(r.id) : next.add(r.id);
    key.set(next);
  }

  togglePage() {
    const check = !this.pageAllChecked();
    if (this.allMatching()) {
      const next = new Set(this.excluded());
      this.rows().forEach((r) => (check ? next.delete(r.id) : next.add(r.id)));
      this.excluded.set(next);
    } else {
      const next = new Set(this.selected());
      this.rows().forEach((r) => (check ? next.add(r.id) : next.delete(r.id)));
      this.selected.set(next);
    }
  }

  selectAllMatching() {
    this.allMatching.set(true);
    this.excluded.set(new Set());
    this.selected.set(new Set());
  }

  clearSelection() {
    this.allMatching.set(false);
    this.selected.set(new Set());
    this.excluded.set(new Set());
  }

  // ── Action ──

  setMode(m: Mode) {
    this.mode.set(m);
  }

  get marginValid(): boolean {
    return this.margin == null || (this.margin >= 0 && this.margin <= 100);
  }

  get canApply(): boolean {
    return (
      this.selectedCount() > 0 &&
      (!!this.vendorId || this.margin != null) &&
      this.marginValid
    );
  }

  get vendorName(): string {
    return this.vendors().find((v) => v.id === this.vendorId)?.name ?? '';
  }

  private request(): BulkVendorAssignRequest {
    const body: BulkVendorAssignRequest = this.allMatching()
      ? { filter: this.appliedFilter, excludeIds: [...this.excluded()] }
      : { productIds: [...this.selected()] };
    if (this.vendorId) {
      body.vendorId = this.vendorId;
      body.mode = this.mode();
      if (this.mode() === 'PRIMARY') {
        body.onlyWithoutVendor = this.onlyWithoutVendor;
        body.dropPreviousVendor = this.dropPreviousVendor;
      }
    }
    if (this.margin != null) body.marginPercentage = Number(this.margin);
    return body;
  }

  openConfirm() {
    if (!this.canApply) return;
    this.confirmOpen.set(true);
    this.refreshPreview();
  }

  closeConfirm() {
    if (this.applying()) return;
    this.confirmOpen.set(false);
    this.preview.set(null);
  }

  refreshPreview() {
    this.previewLoading.set(true);
    this.productService.bulkVendorPreview(this.request()).subscribe({
      next: (p) => {
        this.preview.set(p);
        this.previewLoading.set(false);
      },
      error: (e) => {
        this.previewLoading.set(false);
        this.toastr.error(e?.error?.message || 'Could not preview the update');
      },
    });
  }

  apply() {
    this.applying.set(true);
    this.productService.bulkVendorApply(this.request()).subscribe({
      next: (res) => {
        this.applying.set(false);
        this.confirmOpen.set(false);
        this.preview.set(null);
        const parts = [`${res.updated} product${res.updated === 1 ? '' : 's'} updated`];
        if (res.skipped) parts.push(`${res.skipped} skipped (had another vendor)`);
        this.toastr.success(parts.join(' · '));
        this.margin = null;
        this.clearSelection();
        this.load(this.page());
      },
      error: (e) => {
        this.applying.set(false);
        this.toastr.error(e?.error?.message || 'Update failed');
      },
    });
  }

  // ── View helpers ──

  brandSearch = (term: string, item: any) => {
    const t = term.toLowerCase();
    return (
      (item.name || '').toLowerCase().includes(t) ||
      (item.manufacturer?.name || '').toLowerCase().includes(t)
    );
  };

  get rangeText(): string {
    if (!this.total()) return '0';
    const from = (this.page() - 1) * this.limit + 1;
    const to = Math.min(this.page() * this.limit, this.total());
    return `${from}–${to} of ${this.total()}`;
  }
}
